// [S-413] Record drawer — the slide-over that opens a precedent / certified
// template / Workspace draft the Studio Agent referenced, or that the
// Templates / Precedents / Drafts list screens open on click, beside the
// page content (context preserved). Reuses the existing detail views
// (PrecedentDetail / TemplateDetail / DraftDetail) via their optional id
// props, plus an "open in new tab" escape hatch to the full route.
//
// The agent never shows raw ids (S-412): chips/citations carry RecordRefs whose
// title is shown; this component resolves the ref's id only to fetch + open.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import type { RecordRef } from "../../data/chatQuery";
import { messageText, useChatSessionDetail } from "../../data/chatQuery";
import { useReview } from "../../data/reviewsQuery";
import { Button, LoadingState, Alert } from "../../components";
import { PrecedentDetail } from "../PrecedentDetail";
import { TemplateDetail } from "../TemplateDetail";
import { DraftDetail } from "../DraftDetail";
import { DraftRailEditor } from "./DraftRailEditor";
import { useAgentDraftLock } from "./agentDraftLock";
import { subscribeReviewFocus, getReviewFocus } from "./review";

type RecordDrawerContextValue = {
  /** [S-716] `quote` optionally highlights a specific passage within the
   *  opened record (currently only rendered for an attachment's extracted
   *  text — the source of a draft citation's cited value). */
  openRecord: (ref: RecordRef, quote?: string) => void;
  /** [rail epic] The currently open record, or null. Exposed so a "rail"-mode
   *  caller (Home) can render the panel itself in its own layout AND drive
   *  conversation awareness (what's open right now) — the overlay mode
   *  doesn't need this, it renders the panel internally, but exposing it
   *  unconditionally keeps the context shape the same in both modes. */
  ref: RecordRef | null;
  quote: string | undefined;
  /** [Fix #1] Close the TOP record — pops back to whatever was open before (e.g.
   *  close a citation's source → return to the draft), not a full collapse. */
  close: () => void;
  /** [Fix #1] Clear the whole rail (used on conversation switch). */
  reset: () => void;
};

const RecordDrawerContext = createContext<RecordDrawerContextValue | null>(null);

/** Open the record drawer. Returns inert no-ops when used outside a provider. */
export function useRecordDrawer(): RecordDrawerContextValue {
  return (
    useContext(RecordDrawerContext) ?? {
      openRecord: () => {},
      ref: null,
      quote: undefined,
      close: () => {},
      reset: () => {},
    }
  );
}

/** [rail fix, found live 2026-08-14] Close whatever the drawer/rail is showing
 *  when `key` changes — the caller passes the id of the thing the panel's
 *  contents belong to (Home passes the active conversation id).
 *
 *  The drawer's state lives in the provider, which sits ABOVE the conversation,
 *  so switching chats in the sidebar left the previous conversation's open
 *  attachment sitting in the rail next to a chat that had never mentioned it.
 *  Deliberately keyed on a CHANGE, not on mount, so a caller that mounts with a
 *  record already open doesn't have it closed out from under them. */
export function useCloseRecordOnChange(key: string | null): void {
  // [Fix #1] Switching conversations must clear the WHOLE rail (reset), not just
  // pop one level — the whole stack belonged to the previous conversation.
  const { reset } = useRecordDrawer();
  const prev = useRef(key);
  useEffect(() => {
    if (prev.current === key) return;
    prev.current = key;
    reset();
  }, [key, reset]);
}

/** Full-route href for the "open in new tab" affordance. Empty when the
 *  record type has no standalone route yet (attachment — S-716). */
function fullRoute(ref: RecordRef): string {
  const qs =
    ref.tenantId !== null ? `?tenantId=${encodeURIComponent(ref.tenantId)}` : "";
  switch (ref.type) {
    case "precedent":
      return `/precedent/${encodeURIComponent(ref.id)}${qs}`;
    case "draft":
      return `/drafts/${encodeURIComponent(ref.id)}`;
    case "template":
      return `/library/${encodeURIComponent(ref.id)}`;
    case "attachment":
    case "chat_message":
    case "review":
      return "";
  }
}

function kindLabel(type: RecordRef["type"]): string {
  switch (type) {
    case "precedent":
      return "Precedent";
    case "draft":
      return "Draft";
    case "template":
      return "Certified template";
    case "attachment":
      return "Attachment";
    case "chat_message":
      return "Chat message";
    case "review":
      return "Review against the certified standard";
  }
}

export function RecordDrawerProvider({
  children,
  mode = "overlay",
}: {
  children: ReactNode;
  /** [rail epic] "overlay" (default, unchanged) renders the slide-over modal
   *  below — backdrop, fixed positioning, floats above the page. "rail" drops
   *  all of that; the caller renders the panel itself via <RecordDrawerRail/>,
   *  placed inside its own layout (Home's persistent side column) instead. */
  mode?: "overlay" | "rail";
}): JSX.Element {
  // [Fix #1] The rail is a STACK, not a single slot: opening a draft's citation
  // source PUSHES it on top of the draft, and closing it POPS back to the draft
  // (before, the single slot was nulled → the whole rail collapsed). `ref`/`quote`
  // are just the top of the stack, so every existing consumer is unchanged.
  const [stack, setStack] = useState<
    Array<{ ref: RecordRef; quote: string | undefined }>
  >([]);
  const top = stack.length > 0 ? stack[stack.length - 1]! : null;
  const ref = top?.ref ?? null;
  const quote = top?.quote;
  const openRecord = useCallback((r: RecordRef, q?: string) => {
    // Bring-to-front: drop any existing entry for this record, then push — so
    // re-opening a record moves it to the top instead of stacking duplicates.
    setStack((s) => [...s.filter((e) => e.ref.uri !== r.uri), { ref: r, quote: q }]);
  }, []);
  const close = useCallback(() => {
    setStack((s) => s.slice(0, -1));
  }, []);
  const reset = useCallback(() => setStack([]), []);

  // Esc closes the drawer.
  useEffect(() => {
    if (ref === null) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ref, close]);

  const value = useMemo<RecordDrawerContextValue>(
    () => ({ openRecord, ref, quote, close, reset }),
    [openRecord, ref, quote, close, reset]
  );

  return (
    <RecordDrawerContext.Provider value={value}>
      {children}
      {mode === "overlay" && ref !== null && (
        <div className="rdrawer" role="dialog" aria-modal="true" aria-label={ref.title}>
          <div className="rdrawer__backdrop" onClick={close} />
          <aside className="rdrawer__panel">
            <RecordDrawerHeader recordRef={ref} onClose={close} />
            <div className="rdrawer__body">
              <RecordDrawerBody recordRef={ref} quote={quote} />
            </div>
          </aside>
        </div>
      )}
    </RecordDrawerContext.Provider>
  );
}

/** [rail epic] The rail-mode counterpart to the overlay markup above — a
 *  plain block (no backdrop/fixed positioning) the caller places in its own
 *  layout. Reads the open record straight from context, so mounting it is
 *  the only wiring a rail-mode caller needs; renders nothing when no record
 *  is open, so the caller's layout column collapses naturally. Keyed by
 *  `ref.uri` so switching to a different record always lands back on the
 *  read-only view (RecordDrawerRailPanel's own `editing` state resets with
 *  the remount) rather than carrying a stale edit toggle across records. */
export function RecordDrawerRail(): JSX.Element | null {
  const { ref, quote, close } = useRecordDrawer();
  if (ref === null) return null;
  return (
    <RecordDrawerRailPanel key={ref.uri} recordRef={ref} quote={quote} onClose={close} />
  );
}

function RecordDrawerRailPanel({
  recordRef,
  quote,
  onClose,
}: {
  recordRef: RecordRef;
  quote: string | undefined;
  onClose: () => void;
}): JSX.Element {
  // [rail epic] Story 4 — only a draft has a rail edit surface (precedents
  // stay view-only by design; templates self-manage their own "✎ Edit"
  // toggle inside EditableDocument, S-681). Read-only access is enforced by
  // DraftRailEditor itself (mirrors DraftDetail's Editor), not gated here.
  const [editing, setEditing] = useState(false);
  const canEditHere = recordRef.type === "draft";
  // Entering edit mode is blocked while the agent may be writing this draft
  // (already editing? DraftRailEditor turns read-only and keeps the text).
  const lock = useAgentDraftLock(canEditHere ? recordRef.id : undefined);
  return (
    <aside className="rrail" aria-label={recordRef.title}>
      <RecordDrawerHeader
        recordRef={recordRef}
        onClose={onClose}
        {...(canEditHere
          ? { editing, onToggleEdit: () => setEditing((e) => !e), editLock: editing ? null : lock }
          : {})}
      />
      <div className="rrail__body">
        {canEditHere && editing ? (
          <DraftRailEditor id={recordRef.id} />
        ) : (
          <RecordDrawerBody recordRef={recordRef} quote={quote} />
        )}
      </div>
    </aside>
  );
}

/** Shared header — kind + title + optional edit toggle + "open in new tab" +
 *  close — for both the overlay panel and the rail. `editing`/`onToggleEdit`
 *  are rail-only (the overlay's callers never pass them, so the toggle
 *  simply doesn't render there — the overlay's DraftDetail embed stays
 *  read-only, unchanged). */
function RecordDrawerHeader({
  recordRef,
  onClose,
  editing,
  onToggleEdit,
  editLock,
}: {
  recordRef: RecordRef;
  onClose: () => void;
  editing?: boolean;
  onToggleEdit?: () => void;
  /** Why "✎ Edit" is disabled right now (the agent is writing the draft). */
  editLock?: string | null;
}): JSX.Element {
  return (
    <header className="rdrawer__head">
      <div className="rdrawer__title">
        <span className="rdrawer__kind">{kindLabel(recordRef.type)}</span>
        <span className="rdrawer__name">{recordRef.title}</span>
      </div>
      <div className="rdrawer__actions">
        {onToggleEdit !== undefined && (
          <Button
            variant="ghost"
            className="rdrawer__edit-toggle"
            onClick={onToggleEdit}
            aria-pressed={editing ?? false}
            disabled={editLock != null}
            {...(editLock != null ? { title: editLock } : {})}
          >
            {editing === true ? "Done editing" : "✎ Edit"}
          </Button>
        )}
        {fullRoute(recordRef) !== "" && (
          <a
            className="rdrawer__newtab"
            href={fullRoute(recordRef)}
            target="_blank"
            rel="noreferrer"
            title="Open in a new tab"
          >
            Open in new tab ↗
          </a>
        )}
        <Button
          variant="bare"
          className="rdrawer__close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </Button>
      </div>
    </header>
  );
}

/** Shared body dispatch — resolves a RecordRef to its embedded detail view,
 *  for both the overlay panel and the rail. */
function RecordDrawerBody({
  recordRef,
  quote,
}: {
  recordRef: RecordRef;
  quote: string | undefined;
}): JSX.Element {
  // [after-review-inline P2b] When this draft is the one under an inline review,
  // render it in the EDITOR (one scrollable surface holding every clause) rather
  // than the paginated preview (one page at a time), so the chat can scroll to +
  // outline the clause under review. Same in the overlay and the rail.
  const reviewFocus = useSyncExternalStore(
    subscribeReviewFocus,
    getReviewFocus,
    getReviewFocus
  );
  switch (recordRef.type) {
    case "precedent":
      return (
        <PrecedentDetail
          id={recordRef.id}
          tenantId={recordRef.tenantId ?? undefined}
          embedded
        />
      );
    case "template":
      return <TemplateDetail templateId={recordRef.id} embedded />;
    case "draft":
      return reviewFocus !== null && reviewFocus.draftId === recordRef.id ? (
        <DraftRailEditor id={recordRef.id} />
      ) : (
        <DraftDetail id={recordRef.id} embedded />
      );
    case "attachment":
      return <AttachmentPreview id={recordRef.id} quote={quote} />;
    case "chat_message":
      return <ChatMessagePreview id={recordRef.id} quote={quote} />;
    case "review":
      return <ReviewPreview id={recordRef.id} />;
  }
}

/** [S-766] A certified-standard review the chat started. The review runs in
 *  the background after the agent's reply, so this card is first opened while
 *  the record is still `building` — useReview polls GET /reviews/:id until it
 *  lands, then the findings render here (section · risk · reason · fix). */
function ReviewPreview({ id }: { id: string }): JSX.Element {
  const review = useReview(id);
  if (review.isLoading) return <LoadingState label="Loading the review…" />;
  if (review.isError) {
    return (
      <Alert label="Couldn't load the review" tone="error">
        {review.error.message}
      </Alert>
    );
  }
  const r = review.data;
  if (r === undefined) return <LoadingState label="Loading the review…" />;
  if (r.status === "building") {
    return (
      <div className="rdrawer__pending">
        Reviewing the draft against the certified standard — the findings will
        appear here as soon as the review finishes.
      </div>
    );
  }
  if (r.status === "failed") {
    return (
      <Alert label="The review could not run" tone="error">
        {r.buildError ?? "Try again from the chat."}
      </Alert>
    );
  }
  if (r.templateId === null) {
    return (
      <div className="rdrawer__pending">
        No certified standard was found for this deal type and guild, so there was
        nothing to review against.
      </div>
    );
  }
  return (
    <div className="rdrawer__review">
      <p className="rdrawer__review__summary">
        {r.deviations.length === 0
          ? "No deviations from the certified standard."
          : `${r.deviations.length} deviation${r.deviations.length === 1 ? "" : "s"} from the certified standard.`}
      </p>
      <ol className="rdrawer__review__list">
        {r.deviations.map((d) => (
          <li key={d.findingId} className="rdrawer__review__item">
            <div className="rdrawer__review__head">
              <strong>{d.section !== undefined && d.section.length > 0 ? d.section : d.kind}</strong>
              <span className={`rdrawer__review__risk rdrawer__review__risk--${d.riskLevel}`}>
                {d.riskLevel} risk
              </span>
            </div>
            {d.contractClause.length > 0 ? (
              <blockquote className="rdrawer__review__clause">{d.contractClause}</blockquote>
            ) : null}
            <p className="rdrawer__review__reason">{d.reason}</p>
            {d.suggestedReplacement != null && d.suggestedReplacement.length > 0 ? (
              <p className="rdrawer__review__fix">
                <em>Suggested:</em> {d.suggestedReplacement}
              </p>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** [S-716/chat-message-citations] `ref.id` for both an attachment and a
 *  chat_message is the composite `chatSessionId:{attachmentId|messageId}`
 *  chatAgentTools.ts's buildContextSources builds (see RecordRef.ts's doc
 *  comment) — both live as a field/subcollection scoped under ChatSession,
 *  not a queryable-by-id collection on their own, so the drawer needs the
 *  session id to fetch the one session that owns it. Both halves are UUIDs
 *  (no colons of their own), so splitting on the first `:` is safe. */
function parseCompositeRefId(
  id: string
): { sessionId: string; recordId: string } | null {
  const i = id.indexOf(":");
  if (i <= 0 || i === id.length - 1) return null;
  return { sessionId: id.slice(0, i), recordId: id.slice(i + 1) };
}

function AttachmentPreview({
  id,
  quote,
}: {
  id: string;
  quote?: string;
}): JSX.Element {
  const parsed = parseCompositeRefId(id);
  // Hooks run unconditionally regardless of whether `id` parsed — disabled
  // (sessionId null) when it didn't, same graceful-degrade pattern the rest
  // of the app's query hooks use.
  const session = useChatSessionDetail(parsed?.sessionId ?? null);

  if (parsed === null) {
    return (
      <Alert label="Can't open this attachment" tone="error">
        This citation's reference is malformed.
      </Alert>
    );
  }
  if (session.isLoading) {
    return <LoadingState label="Loading attachment…" />;
  }
  if (session.isError) {
    return (
      <Alert label="Couldn't load the source conversation" tone="error">
        {session.error.message}
      </Alert>
    );
  }
  const attachment = session.data?.session.attachments.find(
    (a) => a.id === parsed.recordId
  );
  if (attachment === undefined) {
    return (
      <Alert label="Attachment not found" tone="error">
        It may have been removed from the conversation it was uploaded to.
      </Alert>
    );
  }
  if (attachment.extractedText.trim().length === 0) {
    return (
      <div className="rdrawer__pending">
        This file's text couldn't be extracted (e.g. a scanned or image-only
        upload) — no preview is available yet.
      </div>
    );
  }
  return (
    <div className="rdrawer__attachment">
      <pre className="rdrawer__attachment__text">
        {highlightQuote(attachment.extractedText, quote)}
      </pre>
    </div>
  );
}

/** [chat-message-citations] The source is a message the user PASTED context
 *  into, rather than an uploaded file — showing just that one message in
 *  isolation lost the surrounding "why," so this renders the WHOLE
 *  conversation (same .sa-msg bubble classes StudioAgents.tsx's live chat
 *  uses, for visual consistency) and auto-scrolls to + highlights the cited
 *  turn. Reuses the SAME useChatSessionDetail fetch the attachment preview
 *  does (the drawer already has `messages` alongside `attachments` from that
 *  one call) — no extra request for showing more context, not less. */
function ChatMessagePreview({
  id,
  quote,
}: {
  id: string;
  quote?: string;
}): JSX.Element {
  const parsed = parseCompositeRefId(id);
  const session = useChatSessionDetail(parsed?.sessionId ?? null);
  const citedRef = useRef<HTMLDivElement | null>(null);

  // Jump to the cited turn once the transcript is in the DOM. `behavior:
  // "smooth"` inside .rdrawer__body's own overflow — no separate scroll
  // container needed, the drawer body already scrolls (studio-agents.css).
  useEffect(() => {
    citedRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [session.data]);

  if (parsed === null) {
    return (
      <Alert label="Can't open this message" tone="error">
        This citation's reference is malformed.
      </Alert>
    );
  }
  if (session.isLoading) {
    return <LoadingState label="Loading conversation…" />;
  }
  if (session.isError) {
    return (
      <Alert label="Couldn't load the source conversation" tone="error">
        {session.error.message}
      </Alert>
    );
  }
  const messages = session.data?.messages ?? [];
  const citedExists = messages.some((m) => m.id === parsed.recordId);
  if (!citedExists) {
    return (
      <Alert label="Message not found" tone="error">
        It may have been removed from the conversation it was sent in.
      </Alert>
    );
  }
  return (
    <div className="rdrawer__transcript">
      {messages.map((m) => {
        const text = messageText(m);
        const isCited = m.id === parsed.recordId;
        return (
          <div
            key={m.id}
            ref={isCited ? citedRef : undefined}
            className={`sa-msg sa-msg--${m.role}${
              isCited ? " rdrawer__transcript__cited" : ""
            }`}
          >
            <div className="sa-msg__bubble">
              {text.trim().length > 0
                ? highlightQuote(text, isCited ? quote : undefined)
                : "(no text — attachment or tool activity only)"}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Wraps the FIRST occurrence of `quote` in the text with a highlight mark —
 *  same first-match spirit as markCitations.ts (the CKEditor plugin's
 *  version), but over plain text producing React nodes directly rather than
 *  an HTML string, since this renders straight into JSX. */
function highlightQuote(text: string, quote: string | undefined): ReactNode {
  if (quote === undefined || quote.trim().length === 0) return text;
  const at = text.indexOf(quote);
  if (at === -1) return text;
  return (
    <>
      {text.slice(0, at)}
      <mark className="rdrawer__attachment__mark">
        {text.slice(at, at + quote.length)}
      </mark>
      {text.slice(at + quote.length)}
    </>
  );
}
